import React, { useContext } from 'react'
import '../PerformanceMonitoring/styles.scss';
import HeaderExtended from '../HeaderExtended'
import usePortfolioName from '../../../../../hooks/usePortfolioName'
import { TagFilterContext } from '../../../../../context/TagFilterContext'
import { Doughnut, HorizontalBar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ReactComponent as IconSatis } from '../../../../../assets/icons/u_alert-check.svg';
import { ReactComponent as IconExcl } from '../../../../../assets/icons/u_alert-exclamacion.svg';
import { ReactComponent as IconMult } from '../../../../../assets/icons/u_alert-multyple.svg';
import { ReactComponent as IconNoAsg } from '../../../../../assets/icons/u_alert-not-assigned.svg';
import HeaderIcons from '../shared/HeaderIcons';
import { textAlign } from 'html2canvas/dist/types/css/property-descriptors/text-align';
import { PerformanceMonitoringData } from '../GeneralInformation/typesExtendedReport';
import { usePerformanceMappedData } from './usePerformanceMappedData';
import PmrDivision from './components/PmrDivision';
import PmrEvolution from './components/PmrEvolution';
import CtClasification from './components/CtClasification';
import PmrDistribution from './components/PmrDistribution';
import ReusableIcon from '../shared/ReusableIcon';

type MonitoringProps = {
    data: PerformanceMonitoringData | null | undefined;
    isLoan?: boolean;
    isTcp?: boolean;
    isIgr?: boolean;
    creationDate?: any;
    regions?: any;
    countries?: string;
    myPortfolio?: string;
    filtersApplied?: any;
}

const titleLon = 'LON';
const titleTcp = 'TCP';
const titleIgr = 'IGR';

const COLORS = {
    satisfactory: '#6DD983',
    alert: '#EF9206',
    problem: '#F06D6D',
    na: '#D0DEF8'
}

const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        datalabels: {
            color: '#000',
            anchor: 'center',
            align: 'bottom',
            font: {
                size: 10
            },
            formatter: function (value: number) {
                return value > 0 ? value : '';
            },
        },
    },
    scales: {
        xAxes: [{
            stacked: true,
            ticks: {
                beginAtZero: true,
                fontSize: 12,
            },
            gridLines: {
                display: false
            }
        }],
        yAxes: [{
            stacked: true,
            gridLines: {
                display: false
            },
            ticks: {
                fontSize: 12,
            },
            barThickness: 10,
            categoryPercentage: 0.8,
            barPercentage: 0.9
        }],
    },
    legend: {
        display: false,
        position: 'bottom',
    },
};

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
        display: false,
        position: 'top'
    },
    plugins: {
        datalabels: {
            display: true,
            anchor: 'end',
            align: 'top',
            formatter: (value) => `${value}%`,
            font: {
                size: 10
            },
        }
    },
    scales: {
        xAxes: [{
            stacked: true,
            ticks: {
                fontSize: 12,
            },
        }],
        yAxes: [{
            ticks: {
                beginAtZero: true,
                fontSize: 12,
                callback: value => `${value}%`
            }
        }],
    }
};


const PerformanceMonitoring: React.FC<MonitoringProps> = ({ data, isLoan, isTcp, isIgr, creationDate, regions, countries, myPortfolio, filtersApplied }) => {
    const { t } = useTranslation();
    const portfolioName = usePortfolioName();
    const { labels } = useContext(TagFilterContext);
    const mp = usePerformanceMappedData(data);
    if (!data || !mp) return null;

    const divisionLabels = mp.PmrDivisions.divisions.map(d => d.divisionName);
    const evolutionLabels = mp.EvolutionPM.map(e => e.year)

    const ICONS_BY_LABEL: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
        [t('one_pager_report.satisfactory')]: IconSatis,
        [t('one_pager_report.alert')]: IconExcl,
        [t('one_pager_report.problems')]: IconMult,
        [t('extended_report.ext-not-assigned')]: IconNoAsg,
    };

    const more3mmLegend = [
        { label: `${t('one_pager_report.satisfactory')}`, value: mp.More3MM.satisfactoryCount, percent: mp.More3MM.satisfactoryPercent, color: COLORS.satisfactory },
        { label: `${t('one_pager_report.alert')}`, value: mp.More3MM.alertCount, percent: mp.More3MM.alertPercent, color: COLORS.alert },
        { label: `${t('one_pager_report.problems')}`, value: mp.More3MM.problemCount, percent: mp.More3MM.problemPercent, color: COLORS.problem },
        { label: `${t('extended_report.ext-not-assigned')}`, value: mp.More3MM.naCount, percent: mp.More3MM.naPercent, color: COLORS.na },
    ];

    const than3mmLegend = [
        { label: `${t('one_pager_report.satisfactory')}`, value: mp.LessThan3MM.satisfactoryCount, percent: mp.LessThan3MM.satisfactoryPercent, color: COLORS.satisfactory },
        { label: `${t('one_pager_report.alert')}`, value: mp.LessThan3MM.alertCount, percent: mp.LessThan3MM.alertPercent, color: COLORS.alert },
        { label: `${t('one_pager_report.problems')}`, value: mp.LessThan3MM.problemCount, percent: mp.LessThan3MM.problemPercent, color: COLORS.problem },
        { label: `${t('extended_report.ext-not-assigned')}`, value: mp.LessThan3MM.naCount, percent: mp.LessThan3MM.naPercent, color: COLORS.na },
    ];

    const chartData = {
        labels: divisionLabels,
        datasets: [
            {
                backgroundColor: COLORS.problem,
                data: mp.PmrDivisions.divisions.map(d => d.problemCount ?? 0),
            },
            {
                backgroundColor: COLORS.alert,
                data: mp.PmrDivisions.divisions.map(d => d.alertCount ?? 0),
            },
            {
                backgroundColor: COLORS.satisfactory,
                data: mp.PmrDivisions.divisions.map(d => d.satisfactoryCount ?? 0),
            },
            {
                backgroundColor: COLORS.na,
                data: mp.PmrDivisions.divisions.map(d => d.naCount ?? 0),
            },
        ],
    };

    const chartDataLine = {
        labels: evolutionLabels,
        datasets: [
            {
                data: mp.EvolutionPM.map(e => e.satisfactoryFinalPercent),
                borderColor: COLORS.satisfactory,
                backgroundColor: COLORS.satisfactory,
                tension: 0.3,
                fill: false,
                pointRadius: 5,
                pointHoverRadius: 7,
            },
            {
                data: mp.EvolutionPM.map(e => e.alertFinalPercent),
                borderColor: COLORS.alert,
                backgroundColor: COLORS.alert,
                tension: 0.3,
                fill: false,
                pointRadius: 5,
                pointHoverRadius: 7,
            },
            {
                data: mp.EvolutionPM.map(e => e.problemFinalPercent),
                borderColor: COLORS.problem,
                backgroundColor: COLORS.problem,
                tension: 0.3,
                fill: false,
                pointRadius: 5,
                pointHoverRadius: 7,
            }
        ],
    };

    const doughnutData = {
        labels: [],
        datasets: [
            {
                data: [
                    mp.More3MM.satisfactoryPercent || 0,
                    mp.More3MM.alertPercent || 0,
                    mp.More3MM.problemPercent || 0,
                    mp.More3MM.naPercent || 0
                ],
                backgroundColor: [COLORS.satisfactory, COLORS.alert, COLORS.problem, COLORS.na],
            },
        ],
    };

    const doughnutoptions = {
        cutoutPercentage: 83,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: false
        },
        plugins: {
            datalabels: false
        }
    };

    const doughnutDataThan = {
        labels: [],
        datasets: [
            {
                data: [
                    mp.LessThan3MM.satisfactoryPercent || 0,
                    mp.LessThan3MM.alertPercent || 0,
                    mp.LessThan3MM.problemPercent || 0,
                    mp.LessThan3MM.naPercent || 0
                ],
                backgroundColor: [COLORS.satisfactory, COLORS.alert, COLORS.problem, COLORS.na],
            },
        ],
    };

    return (
        <>
            <div className='conten-performace-monitoring c-main'>
                <HeaderExtended
                    portfolioName={portfolioName}
                    filtersApplied={filtersApplied ?? labels}
                    creationDate={creationDate}
                    regions={regions}
                    countries={countries}
                    myPortfolio={myPortfolio}
                />
                <section className='performace-monitoring'>
                    <header className="header-content mb-2">
                        <p>{t('extended_report.performance-monitoring')}</p>
                        <div className='flex-avatars'>
                            {(isLoan) && (<ReusableIcon title={titleLon} color="#BE86D8" />)}
                            {(isTcp) && (<ReusableIcon title={titleTcp} color="#66B6B6" />)}
                            {(isIgr) && (<ReusableIcon title={titleIgr} color="#F1AF43" />)}
                        </div>
                    </header>

                    <div className='performace-monitoring-grid'>
                        <section className="left-performace-monitoring card-performace-monitoring">
                            <PmrDivision
                                IconMult={IconMult}
                                IconExcl={IconExcl}
                                IconSatis={IconSatis}
                                IconNoAsg={IconNoAsg}
                                isLoan={isLoan}
                                isIgr={isIgr}
                                chartData={chartData}
                                options={options}
                                mp={mp}
                            />
                        </section>

                        <section className="rtop-performace-monitoring card-performace-monitoring">
                            <PmrEvolution
                                IconMult={IconMult}
                                IconExcl={IconExcl}
                                IconSatis={IconSatis}
                                isLoan={isLoan}
                                isIgr={isIgr}
                                chartDataLine={chartDataLine}
                                chartOptions={chartOptions}
                            />
                        </section>

                        <section className="rbl-performace-monitoring card-performace-monitoring">
                            <CtClasification
                                isTcp={isTcp}
                                than3mmLegend={than3mmLegend}
                                ICONS_BY_LABEL={ICONS_BY_LABEL}
                                doughnutDataThan={doughnutDataThan}
                                doughnutoptions={doughnutoptions}
                            />
                        </section>

                        <section className="rbr-performace-monitoring card-performace-monitoring">
                            <PmrDistribution
                                isLoan={isLoan}
                                isIgr={isIgr}
                                more3mmLegend={more3mmLegend}
                                ICONS_BY_LABEL={ICONS_BY_LABEL}
                                doughnutData={doughnutData}
                                doughnutoptions={doughnutoptions}
                            />
                        </section>
                    </div>
                </section>
            </div>
        </>
    )
}

export default PerformanceMonitoring